"use client";

import { useEffect, useState } from "react";
import { createPortal } from "react-dom";
import Link from "next/link";
import { usePathname } from "next/navigation";
import { Home, Sparkles, Award, Video, User } from "lucide-react";
import { useAuth } from "@/lib/hooks/useAuth";
import { isRealtimeFeedbackPublicEnabled } from "@/lib/config/realtimeFeedbackPublic";

function scrollPageToTop() {
  window.scrollTo({ top: 0, left: 0, behavior: "auto" });
  document.documentElement.scrollTop = 0;
  document.body.scrollTop = 0;
}

export function BottomNavigation() {
  const pathname = usePathname();
  const { user } = useAuth();
  const [mounted, setMounted] = useState(false);

  useEffect(() => {
    setMounted(true);
  }, []);

  useEffect(() => {
    if (!mounted || typeof window === "undefined") return;
    if (!window.matchMedia("(max-width: 767px)").matches) return;

    const tabPrefixes = [
      "/music-analysis",
      "/expert-feedback",
      "/expert-analysis",
      "/help",
      "/contact",
      "/profile",
      "/auth/login",
    ];
    const isTabRoute =
      pathname === "/" ||
      tabPrefixes.some(prefix => pathname?.startsWith(prefix));

    if (isTabRoute) {
      scrollPageToTop();
    }
  }, [pathname, mounted]);

  const navItems = [
    {
      href: "/",
      label: "홈",
      icon: Home,
      matchPaths: ["/"],
    },
    {
      href: "/music-analysis/analyze",
      label: "AI음악분석",
      icon: Sparkles,
      matchPaths: ["/music-analysis"],
    },
    {
      href: "/expert-feedback",
      label: "프로피드백",
      icon: Award,
      matchPaths: [
        "/expert-feedback",
        "/expert-analysis",
        "/profile/expert-analysis",
      ],
    },
    ...(isRealtimeFeedbackPublicEnabled()
      ? [
          {
            href: "/realtime-feedback",
            label: "실시간",
            icon: Video,
            matchPaths: ["/realtime-feedback"],
          },
        ]
      : []),
    {
      href: user ? "/profile" : "/auth/login",
      label: "프로필",
      icon: User,
      matchPaths: ["/profile"],
    },
  ];

  const hidePaths = [
    "/auth/login",
    "/auth/signup",
    "/auth/error",
    "/payment",
    "/chat",
    "/realtime-feedback/demo",
    "/realtime-feedback",
    "/lessons/test-room",
    "/lessons/classroom",
    "/lessons/call",
  ];

  const shouldHide = hidePaths.some(path => pathname?.startsWith(path));

  if (!mounted || shouldHide) {
    return null;
  }

  const nav = (
    <nav
      id="mobile-bottom-nav"
      className="mobile-bottom-nav fixed inset-x-0 bottom-0 z-[10000] md:hidden"
      aria-label="하단 메뉴"
    >
      <div className="border-t border-white/10 bg-[#0c0c10] shadow-[0_-10px_40px_rgba(0,0,0,0.55)]">
        <div className="flex h-16 items-center justify-around px-2">
          {navItems.map(item => {
            const Icon = item.icon;
            const isActive =
              item.href === "/"
                ? pathname === "/"
                : item.matchPaths.some(path => pathname?.startsWith(path));

            const shortLabel = item.label.replace("음악분석", "분석");

            return (
              <Link
                key={item.href}
                href={item.href}
                scroll
                onClick={scrollPageToTop}
                className={`relative flex h-full min-w-0 flex-1 flex-col items-center justify-center gap-0.5 transition-colors ${
                  isActive
                    ? "text-violet-400"
                    : "text-gray-500 hover:text-gray-300"
                }`}
              >
                <Icon
                  className={`h-[22px] w-[22px] shrink-0 transition-transform ${
                    isActive ? "scale-110" : "scale-100"
                  }`}
                  strokeWidth={isActive ? 2.5 : 2}
                />
                <span
                  className={`max-w-full truncate px-0.5 text-[10px] leading-none transition-all ${
                    isActive ? "font-semibold" : "font-medium"
                  }`}
                >
                  {shortLabel}
                </span>
                {isActive && (
                  <div className="absolute bottom-1 left-1/2 h-0.5 w-8 -translate-x-1/2 rounded-full bg-violet-500" />
                )}
              </Link>
            );
          })}
        </div>
        <div
          className="bg-[#0c0c10]"
          style={{
            height: "max(env(safe-area-inset-bottom, 0px), 12px)",
          }}
          aria-hidden
        />
      </div>
    </nav>
  );

  return createPortal(nav, document.body);
}
